/**
 * หน้าจอใบขอซื้อ (Purchase Requisition, P2-2a) — รายการ ค้นหา เปิดใบ และขับ lifecycle
 *
 * ปิดห้าในหกเส้นทางของ contract ที่ `WO-P2-M2-001` เปิดไว้ (D-084):
 * `GET`/`POST /v1/purchase-requisitions` และสาม transition
 * (`submit`/`approve`/`cancel`) ส่วน `GET /{pr_id}` ไม่ถูกเรียกเลย เพราะทุกปุ่ม
 * กระทำต่อ **แถวที่รายการส่งมาแล้ว** และคำตอบของ transition คือใบฉบับล่าสุดอยู่แล้ว
 *
 * โครงของหน้าจอยืมมาจาก `suppliers/SupplierScreen` ทั้งท่อนโดยตั้งใจ — ช่องค้นหาอยู่
 * บนสุด ฟอร์มสร้างอยู่หลังปุ่มในกล่อง `Modal` และหลังบันทึกสำเร็จก็ **โหลดรายการใหม่
 * ผ่าน client เส้นเดิม** ไม่ใช่รีโหลดทั้งหน้า (ไม่มี `window.location.reload` ที่ไหน
 * ในไฟล์นี้) สิ่งที่หน้านี้มีเพิ่มคือ **ปุ่ม lifecycle รายแถว** ซึ่งทะเบียนผู้ขายไม่มี
 * เพราะผู้ขายไม่มี state machine
 *
 * สามข้อที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้:
 *
 * 1. **ปุ่มที่แสดงคือเส้นทางที่มีอยู่จริงในสถานะนั้น** — ใบ `DRAFT` ส่งอนุมัติหรือ
 *    ยกเลิกได้ ใบ `SUBMITTED` อนุมัติหรือยกเลิกได้ ส่วน `APPROVED`/`CONVERTED_TO_PO`/
 *    `CANCELLED` ไม่มีปุ่มเลย (ดู `lifecycleActionsFor`) ปุ่มที่โผล่มาในสถานะที่ไม่มี
 *    เส้นทางคือปุ่มที่กดแล้วได้ `409` ทุกครั้ง
 * 2. **ผู้อนุมัติต้องไม่ใช่ผู้เปิดใบ** (D-010) หน้าจอไม่ซ่อนปุ่ม "อนุมัติ" ให้ผู้เปิดใบ
 *    เพราะหน้าจอไม่รู้ว่าใครกำลังดูอยู่จาก payload ของรายการ — แต่เมื่อเซิร์ฟเวอร์
 *    ปฏิเสธ คำอธิบายจะอยู่ **ที่แถวนั้น** และบอกตรง ๆ ว่าไม่ใช่เรื่องสิทธิ์ไม่พอ
 * 3. **ไม่มีการลบและไม่มีการแก้ใบ** ถอนความต้องการคือการยกเลิก ซึ่งทำให้ใบยังอ่านได้
 *    ต่อไป — ใบสั่งซื้อที่ออกจากใบที่อนุมัติแล้วต้องตามกลับมาหาต้นเรื่องได้เสมอ
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับทุกหน้าจอธุรกิจ: `PROCUREMENT` + `VIEW` เป็นสิทธิ์ของ
 * **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน `CREATE` (เปิดใบ), `EDIT`
 * (ส่งอนุมัติ/ยกเลิก) และ `APPROVE` (อนุมัติ) เป็นสิทธิ์ของ **ปุ่มที่เขียน** ซึ่งถูก
 * บังคับข้างในนี้ทีละปุ่ม ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request
 * เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  approvePurchaseRequisition,
  cancelPurchaseRequisition,
  createPurchaseRequisitionHeader,
  currentStatusFromFailure,
  fetchPurchaseRequisitions,
  ruleFromFailure,
  submitPurchaseRequisition,
  type ApiErrorEnvelope,
  type PurchaseRequisitionFailure,
  type PurchaseRequisitionTransition,
  type PurchaseRequisitionWriteResult,
} from '../../lib/procurement/client';
import {
  PURCHASE_REQUISITION_STATUS_DESCRIPTORS,
  actionLabel,
  actionPermission,
  actionTargetStatus,
  filterPurchaseRequisitions,
  lifecycleActionsFor,
  purchaseRequisitionStatusLabel,
  purchaseRequisitionStatusTone,
  EMPTY_REQUISITION_ITEM,
  EMPTY_REQUISITION_SHEET,
  requiredDateLabel,
  toCreatePurchaseRequisitionHeaderBody,
  type CreateRequisitionItemFields,
  type CreateRequisitionSheetFields,
} from '../../lib/procurement/procurementView';
import {
  APPROVER_MUST_DIFFER_FROM_CREATOR,
  PLANT_OUTSIDE_SCOPE,
  PROCUREMENT_MODULE,
  PRODUCT_NOT_ACTIVE,
  PRODUCT_NOT_FOUND,
  PR_MAX_ITEMS_PER_SHEET,
  PR_NOTE_MAX_LENGTH,
  PR_NUMBER_MAX_LENGTH,
  PR_NUMBER_TAKEN,
  PURCHASE_REQUISITION_FIELD_IS_SERVER_OWNED,
  PURCHASE_REQUISITION_STATUS_CHANGED,
  PURCHASE_REQUISITION_TRANSITION_NOT_ALLOWED,
  PLANT_CODE_MAX_LENGTH,
  PRODUCT_CODE_MAX_LENGTH,
  QUANTITY_MUST_BE_POSITIVE,
  SUGGESTED_SUPPLIER_NOT_FOUND,
  SUPPLIER_CODE_MAX_LENGTH,
  UOM_CODE_MAX_LENGTH,
  UOM_NOT_FOUND,
  type PurchaseRequisition,
  type PurchaseRequisitionHeader,
} from '../../lib/procurement/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type PurchaseRequisitionFailureContext =
  /** อ่านรายการใบขอซื้อ */
  | 'requisitions'
  /** เปิดใบใหม่ */
  | 'create'
  /** กดปุ่ม lifecycle บนแถวใดแถวหนึ่ง */
  | PurchaseRequisitionTransition;

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

/**
 * ประโยค "ไม่มีอะไรเปลี่ยน" ของแต่ละ context ที่ **เขียน** — สตริงว่างสำหรับ context
 * ที่อ่านอย่างเดียว
 *
 * ทุก write ของกลุ่มนี้เขียน `audit_log` (entity_type `PurchaseRequisition`) **ใน
 * ธุรกรรมเดียวกับการเปลี่ยนข้อมูล** ล้มจุดใดก็ไม่เหลือทั้งสองอย่าง — ประโยคนี้จึงเป็น
 * ข้อเท็จจริงของ contract ไม่ใช่คำปลอบใจ
 */
function nothingChangedText(context: PurchaseRequisitionFailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มีใบขอซื้อใบใดถูกเปิดและไม่มีแถว audit ถูกเขียน';
    case 'submit':
    case 'approve':
    case 'cancel':
      return ' ใบนี้ยังอยู่ในสถานะเดิมและไม่มีแถว audit ถูกเขียน';
    case 'requisitions':
      return '';
  }
}

function isWriteContext(context: PurchaseRequisitionFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * ⚠️ ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุลงใน `switch` นี้ — รหัสที่หน้าจอคิดขึ้นเอง
 * คือข้อความที่ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 */
function ruleCopy(rule: string | null, currentStatus: string | null): FailureCopy | null {
  switch (rule) {
    case PR_NUMBER_TAKEN:
      return {
        heading: 'เลขใบขอซื้อนี้มีเจ้าของอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PR_NUMBER_TAKEN} — เลขใบขอซื้อต้องไม่ซ้ำกันทั้งระบบและแก้ไม่ได้หลังเปิดใบ (D-084) ใช้เลขอื่น หรือถ้าใบที่ตั้งใจจะเปิดคือใบที่ถือเลขนี้อยู่แล้ว มันมีอยู่ในรายการด้านล่างแล้ว **ไม่มีใบใดถูกเปิดขึ้น และใบที่ถือเลขนี้ไม่ถูกทับ**`,
      };
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานที่ระบุอยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — ใบขอซื้อเป็นของโรงงานเดียว และคุณเปิดใบให้โรงงานที่ไม่ได้อยู่ในสิทธิ์เข้าถึงของตัวเองไม่ได้ (D-004) ตรวจรหัสโรงงาน หรือขอสิทธิ์เข้าถึงโรงงานนั้นเพิ่ม`,
      };
    case PRODUCT_NOT_FOUND:
      return {
        heading: 'ไม่พบสินค้ารหัสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_FOUND} — ไม่มีสินค้ารหัสนี้ในทะเบียน ตรวจรหัสอีกครั้งจากหน้า "ข้อมูลสินค้า"`,
      };
    case PRODUCT_NOT_ACTIVE:
      return {
        heading: 'สินค้ารหัสนี้ยังไม่พร้อมใช้งาน',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_ACTIVE} — มีสินค้ารหัสนี้อยู่จริงแต่ยังไม่อยู่ในสถานะ ACTIVE (ยังไม่ผ่านอนุมัติ หรือถูกเลิกใช้ไปแล้ว) ต้องให้สินค้าผ่านอนุมัติก่อนจึงจะขอซื้อได้`,
      };
    case UOM_NOT_FOUND:
      return {
        heading: 'ไม่พบหน่วยนับรหัสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${UOM_NOT_FOUND} — หน่วยนับต้องเป็นรหัสที่มีอยู่จริงในทะเบียนหน่วยนับ`,
      };
    case SUGGESTED_SUPPLIER_NOT_FOUND:
      return {
        heading: 'ไม่พบผู้ขายที่เสนอไว้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SUGGESTED_SUPPLIER_NOT_FOUND} — ช่องนี้ไม่บังคับ เว้นว่างไว้ได้ถ้ายังไม่รู้ว่าจะซื้อจากใคร แต่ถ้ากรอกต้องเป็นรหัสที่มีในทะเบียนผู้ขาย (ผู้ขายที่ถูกระงับยังเสนอได้ การห้ามอยู่ที่ใบสั่งซื้อ)`,
      };
    case QUANTITY_MUST_BE_POSITIVE:
      return {
        heading: 'จำนวนต้องมากกว่าศูนย์',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${QUANTITY_MUST_BE_POSITIVE} — ใบขอซื้อจำนวนศูนย์ไม่ได้ขออะไรเลย`,
      };
    case PURCHASE_REQUISITION_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — ฟิลด์ของเซิร์ฟเวอร์ถูกส่งไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${PURCHASE_REQUISITION_FIELD_IS_SERVER_OWNED} — สถานะ ผู้เปิดใบ ผู้อนุมัติ และเวลาทั้งหมดเป็นของเซิร์ฟเวอร์ ไม่มีฟอร์มใดในหน้าจอนี้กรอกมันได้ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก — แจ้งผู้ดูแลระบบ`,
      };
    case PURCHASE_REQUISITION_STATUS_CHANGED:
      return {
        heading: 'มีคนเปลี่ยนสถานะใบนี้ไปก่อนแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PURCHASE_REQUISITION_STATUS_CHANGED} — สถานะที่หน้าจอนี้อ่านมาตอนโหลดไม่ตรงกับที่เก็บอยู่จริง${
          currentStatus === null ? '' : ` ขณะนี้ใบนี้อยู่ที่ ${purchaseRequisitionStatusLabel(currentStatus)}`
        } โหลดรายการใหม่เพื่อดูสถานะจริงก่อนตัดสินใจ — **การกดซ้ำจะได้คำตอบเดิม**`,
      };
    case PURCHASE_REQUISITION_TRANSITION_NOT_ALLOWED:
      return {
        heading: 'สถานะปัจจุบันของใบนี้ไม่มีเส้นทางไปยังสิ่งที่กด',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PURCHASE_REQUISITION_TRANSITION_NOT_ALLOWED} —${
          currentStatus === null ? '' : ` ใบนี้อยู่ที่ ${purchaseRequisitionStatusLabel(currentStatus)} และ`
        } lifecycle ไม่มีเส้นทางนี้ ใบที่อนุมัติแล้วหรือออกใบสั่งซื้อแล้วยกเลิกที่หน้าจอนี้ไม่ได้ — ฝ่ายจัดซื้อลงมือไปแล้ว`,
      };
    case APPROVER_MUST_DIFFER_FROM_CREATOR:
      return {
        heading: 'ผู้อนุมัติต้องไม่ใช่ผู้เปิดใบ',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${APPROVER_MUST_DIFFER_FROM_CREATOR} — **ไม่ใช่เรื่องสิทธิ์ไม่พอ** คุณถือ ${PROCUREMENT_MODULE} + APPROVE ครบแล้ว แต่ใบนี้คุณเป็นผู้เปิดเอง การแบ่งแยกหน้าที่ (D-010) จึงให้คนอื่นเป็นผู้อนุมัติ — ขอสิทธิ์เพิ่มไม่ช่วยอะไร ต้องให้เพื่อนร่วมงานอีกคนกดอนุมัติ **ใบนี้ยังอยู่ที่สถานะเดิม**`,
      };
    default:
      return null;
  }
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็นผู้
 * พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: PurchaseRequisitionFailure,
  context: PurchaseRequisitionFailureContext,
): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);
  const currentStatus = currentStatusFromFailure(failure);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${PROCUREMENT_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่ ขอสิทธิ์เพิ่มที่ตารางสิทธิ์ตามแผนก${nothingChanged}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบใบขอซื้อที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — ใบนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ (สองกรณีนี้ตอบเหมือนกันโดยตั้งใจ) โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'มีของที่ชนกันอยู่แล้วในระบบ',
          text: `เซิร์ฟเวอร์ตอบ 409 — สิ่งที่ส่งไปชนกับสถานะที่เก็บอยู่จริง ไม่ใช่ข้อผิดพลาดของระบบและการกดซ้ำจะได้คำตอบเดิม${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น เลขใบยาวเกิน ${PR_NUMBER_MAX_LENGTH} ตัวอักษร หรือหมายเหตุยาวเกิน ${PR_NOTE_MAX_LENGTH} ตัวอักษร)${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และ **ไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่** — โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงรายการที่อ่านได้ไม่ครบ — ใบที่ขาดไปหนึ่งใบอ่านเหมือน "ยังไม่มีใครขอซื้อของชิ้นนี้" ซึ่งจะพาไปสู่การเปิดใบซ้ำ',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

function envelopeOf(failure: PurchaseRequisitionFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

export interface PurchaseRequisitionFailureNoticeProps {
  readonly failure: PurchaseRequisitionFailure;
  readonly context: PurchaseRequisitionFailureContext;
  readonly onRetry?: () => void;
}

export function PurchaseRequisitionFailureNotice({
  failure,
  context,
  onRetry,
}: PurchaseRequisitionFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  // `409` และ `422` เป็นคำตอบที่ **ผู้ใช้จัดการต่อเองได้** (ใช้เลขอื่น โหลดใหม่ หรือ
  // ให้เพื่อนร่วมงานกดอนุมัติ) ต่างจากความล้มเหลวที่เหลือซึ่งเขาทำอะไรไม่ได้เลย
  const refusedByRule =
    failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="pr-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="pr-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="pr-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface PurchaseRequisitionScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type RequisitionsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly PurchaseRequisition[] }
  | PurchaseRequisitionFailure;

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
type Pending =
  | { readonly kind: 'create' }
  | { readonly kind: PurchaseRequisitionTransition; readonly prId: string };

/** ผลของปุ่ม lifecycle ที่ผูกกับแถวที่กด — ทั้งสำเร็จและไม่สำเร็จอยู่ที่แถวนั้น */
interface ActionOutcome {
  readonly prId: string;
  readonly transition: PurchaseRequisitionTransition;
  readonly failure: PurchaseRequisitionFailure | null;
  readonly requisition: PurchaseRequisition | null;
}

export function PurchaseRequisitionScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: PurchaseRequisitionScreenProps) {
  const canCreate = can(permissions, PROCUREMENT_MODULE, 'CREATE');
  const canEdit = can(permissions, PROCUREMENT_MODULE, 'EDIT');
  const canApprove = can(permissions, PROCUREMENT_MODULE, 'APPROVE');

  const [requisitions, setRequisitions] = useState<RequisitionsState>({
    status: 'loading',
  });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);

  const [query, setQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createFields, setCreateFields] =
    useState<CreateRequisitionSheetFields>(EMPTY_REQUISITION_SHEET);
  const [createFailure, setCreateFailure] = useState<PurchaseRequisitionFailure | null>(
    null,
  );
  const [createdHeader, setCreatedHeader] = useState<PurchaseRequisitionHeader | null>(null);

  const [actionOutcome, setActionOutcome] = useState<ActionOutcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังกดปุ่มสำเร็จ **ตารางเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง:
    // ผู้ใช้เพิ่งกดปุ่มไป สิ่งที่เขาต้องเห็นคือผลของมัน ไม่ใช่หน้าจอที่หายไปทั้งจอ
    setRequisitions((previous) =>
      previous.status === 'ready' ? previous : { status: 'loading' },
    );

    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };

    void fetchPurchaseRequisitions({}, options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setRequisitions(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    // เปิดกล่องใหม่ = เริ่มใหม่: ผลของครั้งก่อน (สำเร็จหรือถูกปฏิเสธ) ไม่ใช่ผลของครั้งนี้
    setCreateFields(EMPTY_REQUISITION_SHEET);
    setCreateFailure(null);
    setCreatedHeader(null);
    setCreateOpen(true);
  };

  /**
   * อ้างอิงคงที่ — `Modal` ผูก/ถอน listener ของ Esc ตาม `onClose` ถ้าฟังก์ชันเปลี่ยน
   * ตัวทุกครั้งที่ผู้ใช้พิมพ์ listener จะถูกผูกใหม่ทุกตัวอักษรโดยไม่จำเป็น
   */
  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const updateSheetField = (field: 'plantCode' | 'remarks', value: string) => {
    setCreateFields((previous) => ({ ...previous, [field]: value }));
  };

  const updateItemField = (
    index: number,
    field: keyof CreateRequisitionItemFields,
    value: string,
  ) => {
    setCreateFields((previous) => ({
      ...previous,
      items: previous.items.map((item, position) =>
        position === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addItem = () => {
    setCreateFields((previous) =>
      previous.items.length >= PR_MAX_ITEMS_PER_SHEET
        ? previous
        : { ...previous, items: [...previous.items, EMPTY_REQUISITION_ITEM] },
    );
  };

  const removeItem = (index: number) => {
    setCreateFields((previous) =>
      previous.items.length <= 1
        ? previous
        : { ...previous, items: previous.items.filter((_item, position) => position !== index) },
    );
  };

  const submitCreate = async () => {
    const body = toCreatePurchaseRequisitionHeaderBody(createFields);
    if (body === null || pending !== null || !canCreate) {
      return;
    }

    setPending({ kind: 'create' });
    setCreateFailure(null);
    setCreatedHeader(null);

    const result = await createPurchaseRequisitionHeader(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย และไม่ปิดกล่อง** — สิ่งที่ผู้ใช้เห็นยังเป็นสถานะที่
      // เซิร์ฟเวอร์ยืนยันล่าสุด และสิ่งที่เขากรอกไว้ยังอยู่ให้แก้แล้วส่งใหม่ได้ทันที
      // ซึ่งสำหรับ `PR_NUMBER_TAKEN` คือสิ่งเดียวที่เขาต้องทำ
      setCreateFailure(result);
      return;
    }

    setCreatedHeader(result.header);
    setCreateOpen(false);
    // คำค้นที่ค้างอยู่ถูกล้างทิ้ง ไม่งั้นใบที่เพิ่งเปิด (ซึ่งมักไม่ตรงกับคำค้นเดิม) จะ
    // ถูกกรองออกทันทีที่โหลดเสร็จ และผู้ใช้จะเห็นคำว่า "เปิดใบแล้ว" คู่กับรายการที่
    // ไม่มีใบนั้นอยู่เลย
    setQuery('');
    setReloadToken((token) => token + 1);
  };

  const runTransition = async (
    requisition: PurchaseRequisition,
    transition: PurchaseRequisitionTransition,
  ) => {
    if (pending !== null || !can(permissions, PROCUREMENT_MODULE, actionPermission(transition))) {
      return;
    }

    setPending({ kind: transition, prId: requisition.pr_id });
    setActionOutcome(null);

    // `expected_status` คือสถานะที่ผู้กด **เห็นอยู่บนจอ** ตอนกด ไม่ใช่สถานะที่เขา
    // อยากให้เป็น — ด่านนี้คือสิ่งเดียวที่แยก "มีคนกดไปก่อนแล้ว" ออกจาก "เส้นทางนี้
    // ไม่มีอยู่จริง" ได้ และรายการที่เปิดค้างไว้เป็นมุมมองที่เก่าได้เสมอ
    const body = { expected_status: requisition.status };
    const options = { apiBaseUrl, fetchImpl };

    let result: PurchaseRequisitionWriteResult;
    switch (transition) {
      case 'submit':
        result = await submitPurchaseRequisition(requisition.pr_id, body, options);
        break;
      case 'approve':
        result = await approvePurchaseRequisition(requisition.pr_id, body, options);
        break;
      case 'cancel':
        result = await cancelPurchaseRequisition(requisition.pr_id, body, options);
        break;
    }

    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะรายการเลย** — สิ่งที่ผู้ใช้เห็นยังเป็นสถานะที่เซิร์ฟเวอร์ยืนยันล่าสุด
      // และคำอธิบายอยู่ที่แถวที่เขากด ไม่ใช่ลอยอยู่ท้ายหน้า
      setActionOutcome({
        prId: requisition.pr_id,
        transition,
        failure: result,
        requisition: null,
      });
      return;
    }

    setActionOutcome({
      prId: requisition.pr_id,
      transition,
      failure: null,
      requisition: result.requisition,
    });
    // สถานะใหม่มาจาก **การโหลดรายการใหม่** ไม่ใช่การแก้แถวในหน่วยความจำ — แถวอื่นอาจ
    // ถูกคนอื่นเปลี่ยนไปแล้วระหว่างที่หน้านี้เปิดค้างอยู่
    setReloadToken((token) => token + 1);
  };

  const items = requisitions.status === 'ready' ? requisitions.items : [];
  const matchedItems = filterPurchaseRequisitions(items, query);
  const busy = pending !== null;
  const createBody = toCreatePurchaseRequisitionHeaderBody(createFields);
  /** ปุ่มที่เขียนได้สักปุ่มหนึ่ง — ใช้ตัดสินว่าจะขึ้นป้าย "ดูได้อย่างเดียว" ไหม */
  const canWriteAnything = canCreate || canEdit || canApprove;

  /** transition ที่แถวนี้เสนอได้จริง = เส้นทางที่มีอยู่ ∩ สิทธิ์ที่ผู้ใช้ถือ */
  const offeredActions = (
    requisition: PurchaseRequisition,
  ): readonly PurchaseRequisitionTransition[] =>
    lifecycleActionsFor(requisition.status).filter((transition) =>
      can(permissions, PROCUREMENT_MODULE, actionPermission(transition)),
    );

  const anyRowHasActions = matchedItems.some(
    (requisition) => offeredActions(requisition).length > 0,
  );

  return (
    <div className="cap-page" data-testid="purchase-requisitions">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ใบขอซื้อ (Purchase Requisition)
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  จุดเริ่มของงานจัดซื้อ — <strong>หนึ่งใบต่อหนึ่งรายการสินค้า</strong> และผูกกับ
                  โรงงานเดียว (D-084) รายการด้านล่างแสดงเฉพาะโรงงานที่คุณมีสิทธิ์เข้าถึง
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="pr-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังเปิดใบหรือขับสถานะไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag{' '}
            <code className="cap-table__code">can_create</code>,{' '}
            <code className="cap-table__code">can_edit</code> หรือ{' '}
            <code className="cap-table__code">can_approve</code> ของโมดูล{' '}
            <code className="cap-table__code">{PROCUREMENT_MODULE}</code> ปุ่ม
            &quot;เปิดใบขอซื้อ&quot; และปุ่มในแต่ละแถวจึงไม่แสดง — รายการและช่องค้นหา
            ยังใช้ได้ตามปกติ
          </p>
          <p className="cap-notice__note">
            การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ
            backend ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {/* ------------------------------------------------- ค้นหา + ปุ่มสร้าง -- */}

      <div className="cap-filters" data-testid="pr-toolbar">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหาใบขอซื้อ</span>
          <input
            className="cap-input"
            type="search"
            name="pr_query"
            data-testid="pr-search"
            value={query}
            autoComplete="off"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <InfoHint
            label="เกี่ยวกับช่องค้นหา"
            text="กรองรายการด้านล่างจากเลขใบหรือรหัสสินค้า — กรองจากรายการที่โหลดมาแล้ว
            ไม่ได้ถามเซิร์ฟเวอร์ใหม่ ช่องว่างหมายถึงแสดงทุกใบ"
          />
        </label>

        {canCreate ? (
          <button
            type="button"
            className="cap-button cap-button--primary"
            data-testid="pr-create-open"
            onClick={openCreate}
          >
            เปิดใบขอซื้อ
          </button>
        ) : null}
      </div>

      {/* --------------------------------------------------------- สร้างใหม่ -- */}

      <Modal open={canCreate && createOpen} onClose={closeCreate} title="เปิดใบขอซื้อใหม่">
        <form
          className="cap-form"
          data-testid="pr-create-form"
          aria-label="เปิดใบขอซื้อใหม่"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <p className="cap-field__hint" data-testid="pr-create-numbering-hint">
            ระบบออกเลขใบขอซื้อให้เอง (PR + ปี เดือน + ลำดับ เช่น PR26100001) และเลขของแต่ละรายการต่อท้ายด้วย
            -001, -002 … ใบเดียวขอซื้อได้หลายรายการ แต่ละรายการส่งอนุมัติและยกเลิกแยกกัน
          </p>

          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              name="plant_code"
              data-testid="pr-create-plant"
              value={createFields.plantCode}
              maxLength={PLANT_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateSheetField('plantCode', event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับรหัสโรงงาน"
              text="ต้องเป็นโรงงานที่คุณมีสิทธิ์เข้าถึง — โรงงานนอกขอบเขตถูกปฏิเสธด้วย 403
              ไม่ใช่เปิดใบแล้วมองไม่เห็น (D-004) ทุกรายการในใบเดียวกันเป็นโรงงานเดียวกัน"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุของใบ (ไม่บังคับ)</span>
            <input
              className="cap-input"
              name="remarks"
              data-testid="pr-create-remarks"
              value={createFields.remarks}
              maxLength={PR_NOTE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                updateSheetField('remarks', event.target.value);
              }}
            />
          </label>

          {createFields.items.map((item, index) => (
            <fieldset
              // ลำดับในรายการคือตัวตนของแถวระหว่างที่ยังไม่ได้ส่ง
              key={index}
              className="cap-card"
              data-testid={`pr-create-item-${index}`}
            >
              <legend className="cap-card__title">รายการที่ {index + 1}</legend>

              <label className="cap-field">
                <span className="cap-field__label">รหัสสินค้า</span>
                <input
                  className="cap-input"
                  name={`product_code_${index}`}
                  data-testid={`pr-create-item-${index}-product`}
                  value={item.productCode}
                  maxLength={PRODUCT_CODE_MAX_LENGTH}
                  autoComplete="off"
                  onChange={(event) => {
                    updateItemField(index, 'productCode', event.target.value);
                  }}
                />
              </label>

              <label className="cap-field">
                <span className="cap-field__label">จำนวน</span>
                <input
                  className="cap-input"
                  name={`quantity_${index}`}
                  data-testid={`pr-create-item-${index}-quantity`}
                  value={item.quantity}
                  inputMode="decimal"
                  autoComplete="off"
                  onChange={(event) => {
                    updateItemField(index, 'quantity', event.target.value);
                  }}
                />
              </label>

              <label className="cap-field">
                <span className="cap-field__label">หน่วยนับ</span>
                <input
                  className="cap-input"
                  name={`uom_code_${index}`}
                  data-testid={`pr-create-item-${index}-uom`}
                  value={item.uomCode}
                  maxLength={UOM_CODE_MAX_LENGTH}
                  autoComplete="off"
                  onChange={(event) => {
                    updateItemField(index, 'uomCode', event.target.value);
                  }}
                />
              </label>

              <label className="cap-field">
                <span className="cap-field__label">วันที่ต้องการของ</span>
                <input
                  className="cap-input"
                  type="date"
                  name={`required_date_${index}`}
                  data-testid={`pr-create-item-${index}-required-date`}
                  value={item.requiredDate}
                  onChange={(event) => {
                    updateItemField(index, 'requiredDate', event.target.value);
                  }}
                />
              </label>

              <label className="cap-field">
                <span className="cap-field__label">ผู้ขายที่เสนอ (ไม่บังคับ)</span>
                <input
                  className="cap-input"
                  name={`suggested_supplier_code_${index}`}
                  data-testid={`pr-create-item-${index}-supplier`}
                  value={item.suggestedSupplierCode}
                  maxLength={SUPPLIER_CODE_MAX_LENGTH}
                  autoComplete="off"
                  onChange={(event) => {
                    updateItemField(index, 'suggestedSupplierCode', event.target.value);
                  }}
                />
              </label>

              <label className="cap-field">
                <span className="cap-field__label">หมายเหตุของรายการ (ไม่บังคับ)</span>
                <input
                  className="cap-input"
                  name={`note_${index}`}
                  data-testid={`pr-create-item-${index}-note`}
                  value={item.note}
                  maxLength={PR_NOTE_MAX_LENGTH}
                  autoComplete="off"
                  onChange={(event) => {
                    updateItemField(index, 'note', event.target.value);
                  }}
                />
              </label>

              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid={`pr-create-item-${index}-remove`}
                disabled={busy || createFields.items.length <= 1}
                onClick={() => {
                  removeItem(index);
                }}
              >
                ลบรายการนี้
              </button>
            </fieldset>
          ))}

          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="pr-create-add-item"
            disabled={busy || createFields.items.length >= PR_MAX_ITEMS_PER_SHEET}
            onClick={addItem}
          >
            + เพิ่มรายการ
          </button>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="pr-create-submit"
              disabled={busy || createBody === null}
            >
              {pending?.kind === 'create' ? 'กำลังเปิดใบ…' : 'เปิดใบขอซื้อ'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="pr-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          {/**
           * ความล้มเหลวอยู่ **ในกล่อง** ข้างฟอร์มที่ทำให้มันเกิด — แก้แล้วกดส่งใหม่ได้ทันทีโดยไม่ต้อง
           * เปิดกล่องใหม่และกรอกใหม่ทั้งหมด; ทุกรายการลงพร้อมกันหรือไม่ลงเลย
           */}
          {createFailure === null ? null : (
            <PurchaseRequisitionFailureNotice failure={createFailure} context="create" />
          )}
        </form>
      </Modal>

      {createdHeader === null ? null : (
        <div className="cap-notice" data-testid="pr-create-result" role="status">
          <StatusBadge tone="success">เปิดใบแล้ว</StatusBadge>
          <h2 className="cap-notice__title">เปิดใบขอซื้อ {createdHeader.header_number} แล้ว</h2>
          <p className="cap-notice__text">
            {createdHeader.items.length} รายการ:{' '}
            {createdHeader.items.map((item) => item.pr_number).join(', ')} —{' '}
            <strong>ยังไม่มีใครเห็นจนกว่าจะกดส่งอนุมัติของแต่ละรายการ</strong>
          </p>
        </div>
      )}

      {/* --------------------------------------------- ผลของปุ่มที่กดสำเร็จ -- */}

      {actionOutcome?.requisition == null ? null : (
        <div className="cap-notice" data-testid="pr-action-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            ใบขอซื้อ {actionOutcome.requisition.pr_number} อยู่ที่สถานะ{' '}
            {purchaseRequisitionStatusLabel(actionOutcome.requisition.status)} แล้ว
          </h2>
          <p className="cap-notice__text">
            สถานะนี้มาจากคำตอบของเซิร์ฟเวอร์ ไม่ใช่จากสิ่งที่หน้าจอเพิ่งส่งไป —
            และรายการด้านล่างถูกโหลดใหม่ทั้งหน้าเพื่อให้เห็นสิ่งที่คนอื่นเปลี่ยนไปด้วย
          </p>
        </div>
      )}

      {/* ------------------------------------------------------- รายการใบ -- */}

      {requisitions.status === 'loading' ? (
        <div className="cap-notice" data-testid="pr-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการใบขอซื้อ…</p>
        </div>
      ) : null}

      {requisitions.status !== 'loading' && requisitions.status !== 'ready' ? (
        <PurchaseRequisitionFailureNotice
          failure={requisitions}
          context="requisitions"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {requisitions.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="pr-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีใบขอซื้อ</StatusBadge>
          <h2 className="cap-notice__title">
            ยังไม่มีใบขอซื้อในโรงงานที่คุณเข้าถึงได้
          </h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {/**
       * "หาไม่เจอ" ไม่ใช่ "ยังไม่มีใบขอซื้อ" และไม่ใช่ "โหลดไม่สำเร็จ"
       *
       * ทั้งสามอย่างให้หน้าจอที่ไม่มีตารางเหมือนกัน แต่สิ่งที่ผู้ใช้ต้องทำต่อคนละเรื่อง
       * กันโดยสิ้นเชิง — อันนี้แก้ด้วยการลบคำค้น ไม่ใช่การเปิดใบใหม่หรือโทรหาไอที
       */}
      {requisitions.status === 'ready' && items.length > 0 && matchedItems.length === 0 ? (
        <div className="cap-notice" data-testid="pr-no-match" role="status">
          <StatusBadge tone="neutral">ไม่พบใบที่ค้นหา</StatusBadge>
          <h2 className="cap-notice__title">
            ไม่มีใบขอซื้อที่ตรงกับ &quot;{query.trim()}&quot;
          </h2>
          <p className="cap-notice__text">
            รายการนี้มีใบขอซื้อ {items.length} ใบ แต่ไม่มีใบใดที่เลขใบหรือรหัสสินค้ามีคำนี้
            อยู่ — ล้างคำค้นเพื่อดูทั้งหมด
          </p>
        </div>
      ) : null}

      {requisitions.status === 'ready' && matchedItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="ใบขอซื้อทั้งหมด">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              ใบขอซื้อทั้งหมด
              <InfoHint
                label="เกี่ยวกับสถานะที่ฟอร์มและปุ่มอ้างถึง"
                text={PURCHASE_REQUISITION_STATUS_DESCRIPTORS.map(
                  (descriptor) => `${descriptor.label} — ${descriptor.summary}`,
                ).join(' / ')}
              />
            </h2>
            <span className="cap-card__hint">
              {query.trim() === ''
                ? 'เรียงตามเลขที่ใบขอซื้อตามที่เซิร์ฟเวอร์ส่งมา'
                : `แสดง ${matchedItems.length} จาก ${items.length} ใบที่ตรงกับคำค้น`}
            </span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="pr-table">
              <caption className="cap-visually-hidden">
                ใบขอซื้อของโรงงานที่คุณเข้าถึงได้ พร้อมสถานะและปุ่มที่สถานะนั้นอนุญาต
              </caption>
              <thead>
                <tr>
                  <th scope="col">เลขที่ใบ</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">สินค้า</th>
                  <th scope="col">จำนวน</th>
                  <th scope="col">ต้องการวันที่</th>
                  <th scope="col">ผู้ขายที่เสนอ</th>
                  <th scope="col">สถานะ</th>
                  {anyRowHasActions ? <th scope="col">การดำเนินการ</th> : null}
                </tr>
              </thead>
              <tbody>
                {matchedItems.map((requisition) => {
                  const actions = offeredActions(requisition);
                  const outcome =
                    actionOutcome?.prId === requisition.pr_id &&
                    actionOutcome.failure !== null
                      ? actionOutcome
                      : null;

                  return (
                    <tr
                      key={requisition.pr_id}
                      data-testid={`pr-row-${requisition.pr_number}`}
                      data-status={requisition.status}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{requisition.pr_number}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{requisition.plant_code}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{requisition.product_code}</code>
                      </td>
                      <td>
                        {requisition.quantity} {requisition.uom_code}
                      </td>
                      <td>{requiredDateLabel(requisition.required_date)}</td>
                      <td>
                        {requisition.suggested_supplier_code === null ? (
                          <span className="cap-table__muted">—</span>
                        ) : (
                          <code className="cap-table__code">
                            {requisition.suggested_supplier_code}
                          </code>
                        )}
                      </td>
                      <td>
                        <StatusBadge
                          tone={purchaseRequisitionStatusTone(requisition.status)}
                        >
                          {purchaseRequisitionStatusLabel(requisition.status)}
                        </StatusBadge>
                      </td>
                      {anyRowHasActions ? (
                        <td>
                          {actions.map((transition) => (
                            <button
                              key={transition}
                              type="button"
                              className="cap-button cap-button--ghost"
                              data-testid={`pr-action-${transition}-${requisition.pr_number}`}
                              disabled={busy}
                              onClick={() => {
                                void runTransition(requisition, transition);
                              }}
                            >
                              {pending?.kind === transition &&
                              pending.prId === requisition.pr_id
                                ? 'กำลังส่ง…'
                                : actionLabel(transition)}
                            </button>
                          ))}

                          {/**
                           * คำอธิบายอยู่ **ที่แถวที่ถูกปฏิเสธ** ไม่ใช่ลอยอยู่ท้ายหน้า
                           * — `APPROVER_MUST_DIFFER_FROM_CREATOR` คือกรณีที่ผู้ใช้ต้อง
                           * เห็นทันทีว่ามันเป็นเรื่องของ **ใบนี้** ไม่ใช่ของสิทธิ์เขา
                           */}
                          {outcome === null || outcome.failure === null ? null : (
                            <div data-testid="pr-action-failure-slot">
                              <PurchaseRequisitionFailureNotice
                                failure={outcome.failure}
                                context={outcome.transition}
                              />
                              <p className="cap-notice__note">
                                ปุ่มที่กดคือ &quot;{actionLabel(outcome.transition)}&quot;
                                ซึ่งพาใบไปที่{' '}
                                {purchaseRequisitionStatusLabel(
                                  actionTargetStatus(outcome.transition),
                                )}
                              </p>
                            </div>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
